import { DevTool } from "@hookform/devtools";
import { FC, PropsWithChildren, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  useConditionalFormRevalidate,
  useCrudEditContext,
  useRideFormPrepare,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  hookFormContextAF,
  IExtendableDto,
  IRideDto,
  KeyboardKeys,
  Log,
  logBoolean,
  rideEditHasMetadataChangedAtom,
  rideHasUnsavedStationChangeAtom,
  shouldDebugForms,
  shouldResetOnAddNew,
  shouldResetOnEditOrDuplicate,
  yupValidationSchemaSF,
} from "@ryde/shared";

import { buildRecordSwitchResetValues } from "./build-record-switch-reset-values";

export const RideEditFormTagWrapper: FC<PropsWithChildren> = ({ children }) => {
  const {
    data: { dto, isEdit, isDuplicate },
    entity,
    mode,
  } = useCrudEditContext<IRideDto>();

  const yupSchemaDynamic = useRecoilValue(yupValidationSchemaSF(entity));
  const yupDynamicResolver = useYupValidationResolver(
    // `undefined`, not a fresh `{}`: the resolver is memoized on this value, so a new literal every
    // render recreates it. The hook already treats a missing schema as an empty shape.
    dto ? yupSchemaDynamic : undefined,
    entity,
  );
  const setHookFormContext = useSetRecoilState(hookFormContextAF(entity));
  const methods = useForm<IExtendableDto>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    resolver: yupDynamicResolver,
  });

  const {
    reset,
    clearErrors,
    control,
    trigger,
    setValue,
    getValues,
    formState: { isDirty, dirtyFields },
  } = methods;

  useEffect(() => {
    setHookFormContext({
      getValues,
      setValue,
      trigger,
      dirtyFields,
      reset,
      clearErrors,
    });
    return () => setHookFormContext(undefined);
  }, [
    clearErrors,
    dirtyFields,
    getValues,
    reset,
    setHookFormContext,
    setValue,
    trigger,
  ]);

  const rideHasUnsavedStationChange = useRecoilValue(
    rideHasUnsavedStationChangeAtom,
  );
  const rideEditHasMetadataChanged = useRecoilValue(
    rideEditHasMetadataChangedAtom,
  );

  useRideFormPrepare();

  useEffect(() => {
    if (isDuplicate && dto && !dto.id) {
      const formId = getValues("id");
      const formRideStart = getValues("rideStart");
      if (formId || !formRideStart) {
        clearErrors();
        Log.debug(`form reset (duplicate) for entity ${entity}, dto:`, dto);
        reset(buildRecordSwitchResetValues(getValues(), dto));
      }
      return;
    }

    const isRecordSwitch = dto?.id !== getValues("id");

    const shouldReset =
      shouldResetOnEditOrDuplicate({ dto, isEdit, isDuplicate }) ||
      shouldResetOnAddNew({ dto, isEdit, isDuplicate });

    if (isRecordSwitch) {
      if (shouldReset) {
        clearErrors();
        Log.debug(`form reset (record switch) for entity ${entity}, dto:`, dto);
        reset(buildRecordSwitchResetValues(getValues(), dto));
      }
      return;
    }

    if (isDirty || rideHasUnsavedStationChange || rideEditHasMetadataChanged) {
      Log.debug(
        `Ride dto changed without form reset, isDirty: ${logBoolean(
          isDirty,
        )}, metadata changed ${logBoolean(
          rideEditHasMetadataChanged,
        )}, has changed stations: ${logBoolean(rideHasUnsavedStationChange)}}`,
      );
      return;
    }

    if (shouldReset) {
      clearErrors();
      Log.debug(`form reset called for entity ${entity}, dto:`, dto);
      reset(buildRecordSwitchResetValues(getValues(), dto));
    }
  }, [
    clearErrors,
    dto,
    entity,
    getValues,
    isDirty,
    isDuplicate,
    isEdit,
    reset,
    rideEditHasMetadataChanged,
    rideHasUnsavedStationChange,
  ]);

  useConditionalFormRevalidate({
    trigger,
    isEdit,
    dto,
    yupSchemaDynamic,
    getValues,
    clearErrors,
  });

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === KeyboardKeys.ENTER) {
      event.preventDefault();
    }
  };

  return (
    <FormProvider {...methods}>
      {/* eslint-disable-next-line jsx-a11y-x/no-noninteractive-element-interactions -- intentional: suppress implicit Enter submit on the form */}
      <form
        onSubmit={handleFormSubmit}
        onKeyDown={handleKeyDown}
        noValidate
        data-e2e="form-tag"
        data-e2e-entity={entity}
        data-e2e-mode={mode}
      >
        {children}
      </form>
      {shouldDebugForms() && <DevTool control={control} />}
    </FormProvider>
  );
};
